/**
 * /admin/waitlist — operator triage page for the public waitlist
 * (OCP-22).
 *
 * Layout: filter sidebar on the left, table on the right. Each row
 * carries a checkbox; select N rows → enable the "Invite selected"
 * + "Decline selected" toolbar buttons. The bulk-invite modal
 * presents the prepaid-code template (tier / category / duration /
 * note) so the operator can review-then-confirm before the actual
 * POST to /v1/admin/waitlist/bulk-invite fires.
 *
 * State model:
 *   - filters: client-side reactive, refetches on change
 *   - selectedIds: Set<string> across re-renders, cleared after a
 *     successful bulk-action
 *   - bulk-invite modal: open/closed + template form state
 *   - decline modal: open/closed + reason input
 *
 * Audit-trail wiring is automatic — the backend logs every
 * mutation under `waitlist.invite` / `waitlist.bulk_invite` /
 * `waitlist.decline` per OCP-21 + OCP-42.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';

import { api, ApiError } from '../lib/api';
import {
  DEFAULT_LICENSE_DURATION,
  LICENSE_DURATION_OPTIONS,
  type LicenseDurationValue,
} from '../lib/license-duration';
import { StatusBadge, formatRelative } from './HomePage';
import { fmtDateTime } from '../lib/format-time';

interface WaitlistItem {
  id: string;
  email: string;
  full_name: string;
  company: string | null;
  role: string | null;
  team_size_bucket: string | null;
  current_tool: string | null;
  most_wanted_features: string[] | null;
  status: string;
  /** OCP-991 - the discount code the person brought, as entered. */
  discount_code?: string | null;
  invite_code_id: string | null;
  invite_mail_sent_at: string | null;
  created_at: string;
  verified_at: string | null;
  invited_at: string | null;
}

const STATUS_OPTIONS = [
  'pending_verification',
  'verified',
  'invited',
  'signed_up',
  'declined',
  'expired',
  'unsubscribed',
] as const;
type StatusOption = (typeof STATUS_OPTIONS)[number];

const TEAM_SIZE_OPTIONS = ['1', '2-10', '11-50', '51-200', '201+'] as const;
type TeamSizeOption = (typeof TEAM_SIZE_OPTIONS)[number];

const CURRENT_TOOL_OPTIONS = [
  'jira',
  'linear',
  'trello',
  'clickup',
  'notion',
  'github',
  'asana',
  'other',
  'none',
] as const;
type CurrentToolOption = (typeof CURRENT_TOOL_OPTIONS)[number];

interface Filters {
  status: StatusOption | '';
  team_size_bucket: TeamSizeOption | '';
  current_tool: CurrentToolOption | '';
}

const EMPTY_FILTERS: Filters = {
  status: '',
  team_size_bucket: '',
  current_tool: '',
};

export function AdminWaitlistPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<WaitlistItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState<{ id: string; email: string } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<
    | { kind: 'single'; id: string; email: string }
    | { kind: 'bulk'; count: number }
    | null
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.team_size_bucket) params.set('team_size_bucket', filters.team_size_bucket);
    if (filters.current_tool) params.set('current_tool', filters.current_tool);
    params.set('limit', '200');
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [filters]);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api<{ items: WaitlistItem[] }>(`/v1/admin/waitlist${queryString}`);
      setItems(r.items);
      // Drop any selection that's no longer in the filtered set.
      setSelected((prev) => {
        const next = new Set<string>();
        for (const it of r.items) if (prev.has(it.id)) next.add(it.id);
        return next;
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setLoadError(`Could not load waitlist (HTTP ${err.status}).`);
      } else {
        setLoadError('Could not load waitlist.');
      }
    }
  }, [queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (!items) return;
    setSelected((prev) => {
      if (prev.size === items.length) return new Set();
      return new Set(items.map((it) => it.id));
    });
  }

  async function onBulkInvite(template: BulkInviteTemplate) {
    setActionError(null);
    try {
      await api('/v1/admin/waitlist/bulk-invite', {
        method: 'POST',
        body: { ids: Array.from(selected), template },
      });
      setBulkInviteOpen(false);
      setSelected(new Set());
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setActionError(`Invite failed (HTTP ${err.status}).`);
      } else {
        setActionError('Invite failed.');
      }
    }
  }

  async function onResend(it: WaitlistItem) {
    if (resendingId) return;
    setActionError(null);
    setResendingId(it.id);
    try {
      // Idempotent server-side: reuses the linked invite_code, never
      // generates a second one (OCP-472).
      await api(`/v1/admin/waitlist/${it.id}/resend-invite`, { method: 'POST' });
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setActionError(`Resend failed (HTTP ${err.status}).`);
      } else {
        setActionError('Resend failed.');
      }
    } finally {
      setResendingId(null);
    }
  }

  async function onDecline(reason: string) {
    if (!declineOpen) return;
    setActionError(null);
    try {
      await api(`/v1/admin/waitlist/${declineOpen.id}/decline`, {
        method: 'POST',
        body: { reason: reason || undefined },
      });
      setDeclineOpen(null);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setActionError(`Decline failed (HTTP ${err.status}).`);
      } else {
        setActionError('Decline failed.');
      }
    }
  }

  async function onDeleteConfirm() {
    if (!deleteOpen) return;
    setActionError(null);
    try {
      if (deleteOpen.kind === 'single') {
        await api(`/v1/admin/waitlist/${deleteOpen.id}`, { method: 'DELETE' });
      } else {
        await api('/v1/admin/waitlist/bulk-delete', {
          method: 'POST',
          body: { ids: Array.from(selected) },
        });
        setSelected(new Set());
      }
      setDeleteOpen(null);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        const msg =
          err.status === 409
            ? 'Cannot delete: this entry has been invited. Decline instead.'
            : `Delete failed (HTTP ${err.status}).`;
        setActionError(msg);
      } else {
        setActionError('Delete failed.');
      }
    }
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      {/* Filter sidebar */}
      <aside className="w-full shrink-0 lg:w-56">
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Filters
            </h2>
            {hasFilters(filters) && (
              <button
                type="button"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="text-xs text-brand-500 hover:text-brand-600"
              >
                Reset
              </button>
            )}
          </div>
          <FilterSelect
            label="Status"
            value={filters.status}
            options={STATUS_OPTIONS}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          />
          <FilterSelect
            label="Team size"
            value={filters.team_size_bucket}
            options={TEAM_SIZE_OPTIONS}
            onChange={(v) => setFilters((f) => ({ ...f, team_size_bucket: v }))}
          />
          <FilterSelect
            label="Current tool"
            value={filters.current_tool}
            options={CURRENT_TOOL_OPTIONS}
            onChange={(v) => setFilters((f) => ({ ...f, current_tool: v }))}
          />
        </div>
      </aside>

      {/* Main */}
      <main className="min-w-0 flex-1">
        <div className="mb-4 flex items-baseline justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Waitlist</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {items === null
                ? 'Loading…'
                : `${items.length} ${items.length === 1 ? 'entry' : 'entries'} matching the current filters`}
            </p>
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {selected.size} selected
              </span>
              <button
                type="button"
                onClick={() => setBulkInviteOpen(true)}
                className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700"
              >
                Invite selected
              </button>
              <button
                type="button"
                onClick={() => setDeleteOpen({ kind: 'bulk', count: selected.size })}
                className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50"
              >
                Delete selected
              </button>
            </div>
          )}
        </div>

        {actionError && (
          <div
            role="alert"
            className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            {actionError}
          </div>
        )}
        {loadError && (
          <div
            role="alert"
            className="mb-4 flex items-center justify-between rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            <span>{loadError}</span>
            <button
              type="button"
              onClick={() => void load()}
              className="rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        )}

        <WaitlistTable
          items={items}
          selected={selected}
          resendingId={resendingId}
          onToggleOne={toggleOne}
          onToggleAll={toggleAll}
          onResend={onResend}
          onDecline={(it) => setDeclineOpen({ id: it.id, email: it.email })}
          onDelete={(it) => setDeleteOpen({ kind: 'single', id: it.id, email: it.email })}
        />
      </main>

      {bulkInviteOpen && (
        <BulkInviteModal
          selectedCount={selected.size}
          selectedBuckets={(items ?? []).filter((it) => selected.has(it.id)).map((it) => it.team_size_bucket)}
          onCancel={() => setBulkInviteOpen(false)}
          onConfirm={onBulkInvite}
        />
      )}
      {declineOpen && (
        <DeclineModal
          email={declineOpen.email}
          onCancel={() => setDeclineOpen(null)}
          onConfirm={onDecline}
        />
      )}
      {deleteOpen && (
        <DeleteConfirmModal
          target={deleteOpen}
          onCancel={() => setDeleteOpen(null)}
          onConfirm={onDeleteConfirm}
        />
      )}
    </div>
  );
}

// ---- helpers ----------------------------------------------------

function hasFilters(f: Filters): boolean {
  return Boolean(f.status || f.team_size_bucket || f.current_tool);
}

function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | '';
  options: readonly T[];
  onChange: (v: T | '') => void;
}) {
  return (
    <label className="mb-3 flex flex-col gap-1 text-xs">
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T | '')}
        className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
    </label>
  );
}

function WaitlistTable({
  items,
  selected,
  resendingId,
  onToggleOne,
  onToggleAll,
  onResend,
  onDecline,
  onDelete,
}: {
  items: WaitlistItem[] | null;
  selected: Set<string>;
  resendingId: string | null;
  onToggleOne: (id: string) => void;
  onToggleAll: () => void;
  onResend: (it: WaitlistItem) => void;
  onDecline: (it: WaitlistItem) => void;
  onDelete: (it: WaitlistItem) => void;
}) {
  if (items === null) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        No entries match the current filters.
      </div>
    );
  }
  const allSelected = selected.size === items.length;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
          <tr>
            <th className="w-10 px-3 py-2">
              <input
                type="checkbox"
                aria-label="Select all rows"
                checked={allSelected}
                onChange={onToggleAll}
                className="h-4 w-4 accent-brand-500"
              />
            </th>
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Company</th>
            <th className="px-3 py-2">Team</th>
            <th className="px-3 py-2">Tool</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2 text-right">Signed up</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {items.map((it) => (
            <tr
              key={it.id}
              className={selected.has(it.id) ? 'bg-brand-50/50' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'}
            >
              <td className="px-3 py-2">
                <input
                  type="checkbox"
                  aria-label={`Select ${it.email}`}
                  checked={selected.has(it.id)}
                  onChange={() => onToggleOne(it.id)}
                  className="h-4 w-4 accent-brand-500"
                />
              </td>
              <td className="truncate px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                {it.email}
                {it.discount_code && (
                  <div className="mt-0.5 font-mono text-xs font-normal text-gray-500" data-testid="waitlist-discount-code">{it.discount_code}</div>
                )}
              </td>
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{it.full_name || '—'}</td>
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{it.company || '—'}</td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{it.team_size_bucket || '—'}</td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{it.current_tool || '—'}</td>
              <td className="px-3 py-2">
                <StatusBadge status={it.status} />
                {it.invite_code_id && (
                  <div className="mt-1">
                    <InviteMailStatus sentAt={it.invite_mail_sent_at} />
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                {formatRelative(it.created_at)}
              </td>
              <td className="px-3 py-2 text-right">
                <div className="flex justify-end gap-3">
                  {it.invite_code_id && (
                    <button
                      type="button"
                      onClick={() => onResend(it)}
                      disabled={resendingId === it.id}
                      title="Re-send the invite email (reuses the existing code, no new code is generated)"
                      className="text-xs text-brand-500 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid={`resend-invite-${it.id}`}
                    >
                      {resendingId === it.id ? 'Resending…' : 'Resend invite'}
                    </button>
                  )}
                  {it.status !== 'declined' && it.status !== 'unsubscribed' && (
                    <button
                      type="button"
                      onClick={() => onDecline(it)}
                      className="text-xs text-gray-500 hover:text-rose-600"
                    >
                      Decline
                    </button>
                  )}
                  {!it.invite_code_id && (
                    <button
                      type="button"
                      onClick={() => onDelete(it)}
                      title="Hard-delete this row"
                      className="text-xs text-gray-500 hover:text-rose-700"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Per-invited-row invite-mail status (OCP-472). `sentAt` set -> the
 * `waitlist.send-invite` job delivered the mail (green + timestamp);
 * null on an invited row -> still queued or the last send failed and
 * pg-boss is retrying (amber). Full delivery state lives in OMS
 * (ocp_mail_operator_sends); this is the at-a-glance row signal.
 */
function InviteMailStatus({ sentAt }: { sentAt: string | null }) {
  if (sentAt) {
    // OCP-473: icon-only so the sent state stays quiet in the list; the
    // exact send time lives in the hover tooltip.
    return (
      <span
        data-testid="invite-mail-status"
        data-state="sent"
        role="img"
        aria-label={`Invite mail sent ${fmtDateTime(sentAt)}`}
        title={`Invite mail sent ${fmtDateTime(sentAt)}`}
        className="inline-flex items-center text-xs text-emerald-600 dark:text-emerald-400"
      >
        <span aria-hidden>&#10003;</span>
      </span>
    );
  }
  return (
    <span
      data-testid="invite-mail-status"
      data-state="queued"
      role="img"
      aria-label="Invite mail queued"
      title="The invite mail is queued; it will send shortly (pg-boss)."
      className="inline-flex items-center text-xs text-amber-600 dark:text-amber-400"
    >
      <span aria-hidden>&#9201;</span>
    </span>
  );
}

// ---- Bulk-invite modal -----------------------------------------

const TIER_OPTIONS = ['team', 'business'] as const;
type TierOption = (typeof TIER_OPTIONS)[number];

const CATEGORY_OPTIONS = [
  'early_adopter',
  'beta_tester',
  'friend_family',
  'goodwill_refund',
  'conference_giveaway',
  'partner',
  'direct_sale',
] as const;
type CategoryOption = (typeof CATEGORY_OPTIONS)[number];

interface BulkInviteTemplate {
  tier: TierOption;
  category: CategoryOption;
  duration: LicenseDurationValue;
  /** OCP-625 - the purchased seat cap the minted code carries (required). */
  seats: number;
  note?: string;
}

/**
 * OCP-625 - the seat default for the invite: the lower bound of the
 * entry's team-size bucket ("11-50" -> 11, "250-999" -> 250, "1000+" ->
 * 1000), 5 for the smallest bucket or no bucket. With several entries
 * selected the default is taken only when they all share one bucket.
 */
export function defaultSeatsForBuckets(buckets: Array<string | null | undefined>): number {
  const distinct = Array.from(new Set(buckets.map((b) => (b ?? '').trim()).filter((b) => b.length > 0)));
  if (distinct.length !== 1) return 5;
  const m = /^(\d+)/.exec(distinct[0]!);
  if (!m) return 5;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n <= 1) return 5;
  return Math.min(1000, n);
}

function BulkInviteModal({
  selectedCount,
  selectedBuckets,
  onCancel,
  onConfirm,
}: {
  selectedCount: number;
  /** team_size_bucket of every selected entry (OCP-625 seat default). */
  selectedBuckets: Array<string | null | undefined>;
  onCancel: () => void;
  onConfirm: (t: BulkInviteTemplate) => Promise<void>;
}) {
  const [tier, setTier] = useState<TierOption>('team');
  const [category, setCategory] = useState<CategoryOption>('beta_tester');
  const [duration, setDuration] = useState<LicenseDurationValue>(DEFAULT_LICENSE_DURATION);
  const [seats, setSeats] = useState<string>(String(defaultSeatsForBuckets(selectedBuckets)));
  const [note, setNote] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const seatsNumber = Number(seats);
  const seatsValid = Number.isInteger(seatsNumber) && seatsNumber >= 1 && seatsNumber <= 1000;
  const durationLabel = LICENSE_DURATION_OPTIONS.find((o) => o.value === duration)?.label ?? duration;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting || !seatsValid) return;
    setSubmitting(true);
    try {
      await onConfirm({
        tier,
        category,
        duration,
        seats: seatsNumber,
        note: note.trim() || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Invite selected entries"
      subtitle={`${selectedCount} ${selectedCount === 1 ? 'entry' : 'entries'} will receive an invite code.`}
      onClose={onCancel}
    >
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <ModalRow label="Tier">
          <SelectLike
            value={tier}
            options={TIER_OPTIONS}
            onChange={(v) => setTier(v)}
          />
        </ModalRow>
        <ModalRow label="Category">
          <SelectLike
            value={category}
            options={CATEGORY_OPTIONS}
            onChange={(v) => setCategory(v)}
          />
        </ModalRow>
        <ModalRow label="Duration">
          <select
            data-testid="duration-select"
            value={duration}
            onChange={(e) => setDuration(e.target.value as LicenseDurationValue)}
            className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          >
            {LICENSE_DURATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </ModalRow>
        <ModalRow label="Seats (purchased seat cap)">
          <input
            type="number"
            min={1}
            max={1000}
            step={1}
            required
            value={seats}
            onChange={(e) => setSeats(e.target.value)}
            data-testid="invite-seats"
            className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          <span className="text-xs text-gray-500">
            The seat cap on the minted code: the tenant&apos;s seat entitlement, its AI allowance
            (tier x seats) and the billable-seat comparison resolve against it. Default from the
            entry&apos;s team-size bucket, else 5.
          </span>
        </ModalRow>
        <p className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-xs text-gray-700 dark:text-gray-300" data-testid="invite-entitlement">
          Entitlement: <span className="font-medium capitalize">{tier}</span>, {durationLabel},{' '}
          {seatsValid ? `${seatsNumber} seat${seatsNumber === 1 ? '' : 's'}` : 'seats missing'}
        </p>
        <ModalRow label="Note (internal)">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="e.g. first beta wave"
            className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
        </ModalRow>
        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !seatsValid}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {submitting ? 'Inviting…' : `Send ${selectedCount} invite${selectedCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DeclineModal({
  email,
  onCancel,
  onConfirm,
}: {
  email: string;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(reason);
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <Modal title="Decline waitlist entry" subtitle={email} onClose={onCancel}>
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <ModalRow label="Reason (internal note, optional)">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. spam signature pattern, company is competitor, …"
            className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
        </ModalRow>
        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {submitting ? 'Declining…' : 'Decline'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteConfirmModal({
  target,
  onCancel,
  onConfirm,
}: {
  target:
    | { kind: 'single'; id: string; email: string }
    | { kind: 'bulk'; count: number };
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  async function submit() {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  }
  const title =
    target.kind === 'single' ? 'Delete waitlist entry' : 'Delete selected entries';
  const subtitle =
    target.kind === 'single'
      ? target.email
      : `${target.count} ${target.count === 1 ? 'entry' : 'entries'} will be hard-deleted.`;
  return (
    <Modal title={title} subtitle={subtitle} onClose={onCancel}>
      <div className="flex flex-col gap-3 text-sm">
        <p className="text-gray-700 dark:text-gray-300">
          This permanently removes the row{target.kind === 'bulk' ? 's' : ''} from
          the database. There is no undo.
        </p>
        <p className="text-xs text-gray-500">
          Entries that have already been invited are skipped server-side — for
          those, decline + revoke the linked code instead. This deletion is
          recorded in the audit log.
        </p>
        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="rounded bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {submitting
              ? 'Deleting…'
              : target.kind === 'single'
                ? 'Delete'
                : `Delete ${target.count}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Modal primitives ------------------------------------------

function Modal({
  title,
  subtitle,
  children,
  onClose,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
      {children}
    </label>
  );
}

function SelectLike<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o.replace(/_/g, ' ')}
        </option>
      ))}
    </select>
  );
}
